/**
 * The playable game, in a sandboxed iframe.
 *
 * ## How the agent's code gets on screen
 *
 * The service serves a complete HTML document per session at
 * `/coder/game/:sessionId?v=N` (see service/src/coder/game/gameRoute.ts). This
 * component points an iframe at that URL and talks to it over postMessage:
 * `{type:"start"}` / `{type:"stop"}` out, `gameReady` / `gameError` back.
 *
 * `v` is the game version from the store. Because it is part of the `src`, a
 * build bumping the version gives the iframe a brand-new URL — which is what
 * reliably swaps in new code. (Same URL + reload is at the mercy of the HTTP
 * cache, and "I pressed Build and nothing changed" is the failure mode.)
 *
 * ## Sandbox
 *
 * `sandbox="allow-scripts"` WITHOUT `allow-same-origin` is the load-bearing
 * control: the game runs on a unique opaque origin, so model-authored code
 * cannot touch this page, its DOM, its storage, or its cookies. The served
 * document also carries a CSP denying all network access. Prototype-grade, but
 * these are the two mitigations that actually matter.
 */

import React, { useEffect, useRef, useState } from "react";
import { View, Text, Pressable, StyleSheet, Platform } from "react-native";
import { colors, textStyle } from "../styles";
import { getVoiceSocketUrl } from "../utils/AppConfig";
import { useCoderStore } from "./coderStore";

export default function GameView() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // The SERVER's session id, not socketStore's local one — see coderStore.
  const gameSessionId = useCoderStore((s) => s.gameSessionId);
  const gameVersion = useCoderStore((s) => s.gameVersion);
  const isPlaying = useCoderStore((s) => s.isPlaying);
  const buildStatus = useCoderStore((s) => s.buildStatus);
  const buildStep = useCoderStore((s) => s.buildStep);
  /** True once we've waited long enough that "Loading..." is really a failure. */
  const [loadTimedOut, setLoadTimedOut] = useState(false);

  // Arm a timeout while we have no session id, and clear it the moment one
  // arrives. Re-runs when gameSessionId changes so a retry resets the state.
  useEffect(() => {
    if (gameSessionId) {
      setLoadTimedOut(false);
      return;
    }
    const t = setTimeout(() => setLoadTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [gameSessionId]);

  // Relay start/stop into the iframe whenever the play state changes.
  useEffect(() => {
    if (Platform.OS !== "web") return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: isPlaying ? "start" : "stop" },
      "*",
    );
  }, [isPlaying]);

  // Surface game-side errors (a bad build that still parsed but throws at
  // runtime) instead of leaving a black rectangle.
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const onMessage = (e: MessageEvent) => {
      const d = (e.data ?? {}) as { type?: string; message?: string; kind?: string };
      if (d.type === "gameError") {
        useCoderStore.setState({
          isPlaying: false,
          error: `Game error: ${d.kind ?? ""} ${d.message ?? ""}`.trim(),
        });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  if (Platform.OS !== "web") {
    return (
      <View style={[styles.box, styles.center]}>
        <Text style={styles.placeholder}>The game runs in the web app.</Text>
      </View>
    );
  }

  const building = buildStatus === "building";

  // Wait for the server to tell us which session the game lives under; loading
  // the iframe before then would seed and show an unrelated baseline game.
  //
  // But don't wait forever: if the backend is down or the socket never connects,
  // this used to sit on "Loading your game..." with no explanation and no way
  // out, even across refreshes. After a few seconds, say what's wrong and offer
  // a retry.
  if (!gameSessionId) {
    return (
      <View style={[styles.box, styles.center]}>
        {loadTimedOut ? (
          <>
            <Text style={styles.placeholder}>
              Can&apos;t reach the game server.
            </Text>
            <Pressable
              style={styles.retryBtn}
              onPress={() => {
                setLoadTimedOut(false);
                useCoderStore.getState().listNotes().catch(() => {});
              }}
              testID="game-retry"
            >
              <Text style={styles.retryBtnText}>Try again</Text>
            </Pressable>
          </>
        ) : (
          <Text style={styles.placeholder}>Loading your game...</Text>
        )}
      </View>
    );
  }

  const src = `${getVoiceSocketUrl()}/coder/game/${encodeURIComponent(gameSessionId)}?v=${gameVersion}`;

  return (
    <View style={styles.box}>
      <iframe
        // Keying on the version forces a fresh element (and a fresh document)
        // for every build — no stale-cache ambiguity.
        key={`${gameSessionId}:${gameVersion}`}
        ref={iframeRef}
        src={src}
        title="Your game"
        sandbox="allow-scripts"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: "block",
          borderRadius: 12,
          background: "#181A2A",
        }}
      />

      {!isPlaying && !building ? (
        <Pressable
          style={styles.overlay}
          onPress={() => useCoderStore.getState().setPlaying(true)}
          testID="game-overlay-play"
        >
          <View style={styles.playBadge}>
            <Text style={styles.playBadgeText}>{"▶"}  Tap to play</Text>
          </View>
          <Text style={styles.hint}>
            Arrow keys or WASD to move · Up / Space to jump
          </Text>
        </Pressable>
      ) : null}

      {building ? (
        <View style={styles.overlay} pointerEvents="none">
          <View style={styles.buildBadge}>
            <Text style={styles.buildTitle}>Building your game...</Text>
            <Text style={styles.buildStep}>{buildStep || "Thinking..."}</Text>
            <Text style={styles.buildHint}>This can take a minute.</Text>
          </View>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  box: {
    flex: 1,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "#181A2A",
    borderWidth: 1,
    borderColor: colors.border,
    position: "relative",
  },
  center: { alignItems: "center", justifyContent: "center", gap: 12 },
  placeholder: { color: colors.textMuted, ...textStyle.body },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(24,26,42,0.72)",
    gap: 14,
  },
  playBadge: {
    paddingHorizontal: 28,
    paddingVertical: 14,
    borderRadius: 28,
    backgroundColor: colors.accent,
  },
  playBadgeText: { color: colors.white, fontSize: 20, ...textStyle.heading },
  hint: { color: "#C9C7E8", fontSize: 13, ...textStyle.body },
  buildBadge: { alignItems: "center", gap: 8, paddingHorizontal: 24 },
  buildTitle: { color: colors.white, fontSize: 22, ...textStyle.heading },
  buildStep: { color: "#C9C7E8", fontSize: 15, ...textStyle.body },
  buildHint: { color: colors.textMuted, fontSize: 13, ...textStyle.body },
  retryBtn: {
    paddingHorizontal: 18,
    paddingVertical: 9,
    borderRadius: 18,
    backgroundColor: colors.accent,
  },
  retryBtnText: { color: colors.white, fontSize: 14, ...textStyle.heading },
});
